import React, { useState, useEffect } from "react";

export default function BugReportModal({ open, onClose, defaultType = "bug" }) {
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [minimized, setMinimized] = useState(false); // 🔹 new

  // Reset minimized state when closed from parent
  useEffect(() => {
    if (!open) {
      setMinimized(false);
      setOk(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  // 🔹 Minimized "dock" view – keeps form state in memory, just hidden
  if (minimized) {
    return (
      <button
        type="button"
        className="help-dock"
        onClick={() => setMinimized(false)}
        aria-label="Reopen bug report"
      >
        🐞 Bug report (draft saved)
      </button>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setErr("");
    setSending(true);
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          title,
          message,
          page: window.location.pathname + window.location.search,
          extra: {
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });
      if (!res.ok) throw new Error("Send failed");
      setOk(true);
      setTitle("");
      setMessage("");
    } catch (e) {
      setErr(e.message || "Could not send feedback.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="help-overlay" role="dialog" aria-modal="true" aria-label="Report a bug">
      <div className="help-modal">
        <div className="help-head">
          <h3>Report a bug</h3>
          <div className="help-head-actions">
            {/* 🔹 new: minimize button */}
            <button
              type="button"
              className="help-min-btn"
              onClick={() => setMinimized(true)}
              aria-label="Hide bug report"
              title="Hide (keep draft)"
            >
              —
            </button>
            <button className="help-x" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {ok ? (
          <div className="help-success">
            ✅ Thanks! Your report was sent.
            <div>
              <button
                className="help-btn"
                onClick={() => {
                  setOk(false);
                  onClose();
                }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="help-form">
            <label className="help-row">
              <span>Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="bug">Bug</option>
                <option value="idea">Idea</option>
                <option value="other">Other</option>
              </select>
            </label>

            <label className="help-row">
              <span>Title</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary"
                required
              />
            </label>

            <label className="help-row">
              <span>Description</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened? Steps to reproduce?"
                rows={6}
                required
              />
            </label>

            {err && <div className="help-error">{err}</div>}

            <div className="help-actions">
              <button
                type="button"
                className="help-btn ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="help-btn primary"
                disabled={sending}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
